/**
 * Component: AIPerformanceAnalysisInteractive
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Sparkles, ChevronRight, TrendingUp, Users, Target, DollarSign, Zap } from 'lucide-react';

interface AIInsights {
  observations?: string[];
  pricing_opportunities?: string[];
  menu_recommendations?: string[];
  insights?: Array<string | { type: string; title: string; content: string }>;
}

interface AIPerformanceAnalysisInteractiveProps {
  insights: AIInsights;
  loading?: boolean;
}

export function AIPerformanceAnalysisInteractive({ insights, loading }: AIPerformanceAnalysisInteractiveProps) {
  const [currentState, setCurrentState] = useState<'entry' | 'thinking' | 'revealing'>('entry');
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
  const [thinkingProgress, setThinkingProgress] = useState(0);

  // Process insights into a unified array
  const processedInsights = React.useMemo(() => {
    const allInsights = [];
    
    // Handle new format with insights array
    if (insights?.insights?.length) {
      insights.insights.forEach((item, index) => {
        if (typeof item === 'string') {
          allInsights.push({
            title: `Insight ${index + 1}`,
            content: item,
            category: 'Analysis',
            icon: 'zap'
          });
        } else if (item && typeof item === 'object') {
          allInsights.push({
            title: item.title || `Insight ${index + 1}`,
            content: item.content || '',
            category: item.type || 'Analysis',
            icon: item.type === 'pricing' ? 'dollar' : item.type === 'customer' ? 'users' : 'target'
          });
        }
      });
    }
    
    // Handle old format with separate arrays
    if (insights?.pricing_opportunities?.length) {
      insights.pricing_opportunities.forEach((item) => {
        allInsights.push({
          title: 'Price Point Analysis',
          content: item,
          category: 'Pricing',
          icon: 'dollar'
        });
      });
    }
    
    if (insights?.observations?.length) {
      insights.observations.forEach((item) => {
        allInsights.push({
          title: 'Customer Preferences',
          content: item,
          category: 'customer_behavior',
          icon: 'users'
        });
      });
    }
    
    if (insights?.menu_recommendations?.length) {
      insights.menu_recommendations.forEach((item) => {
        allInsights.push({
          title: 'Strategic Opportunity',
          content: item,
          category: 'Strategy',
          icon: 'target'
        });
      });
    }
    
    return allInsights;
  }, [insights]);

  // Reset when insights change
  useEffect(() => {
    setCurrentState('entry');
    setCurrentInsightIndex(0);
    setThinkingProgress(0);
  }, [insights]);

  const handleStartAnalysis = () => {
    setCurrentState('thinking');
    setThinkingProgress(0);
    
    // Simulate AI thinking with progress updates
    const progressInterval = setInterval(() => {
      setThinkingProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => setCurrentState('revealing'), 200);
          return 100;
        }
        return prev + 20;
      });
    }, 400);
  };

  const handleNextInsight = () => {
    if (processedInsights && currentInsightIndex < processedInsights.length - 1) {
      setCurrentInsightIndex(prev => prev + 1);
    }
  };

  const handlePreviousInsight = () => {
    if (currentInsightIndex > 0) {
      setCurrentInsightIndex(prev => prev - 1);
    }
  };

  const formatCategoryLabel = (category: string) => {
    // Convert snake_case or any format to Title Case
    return category
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/-/g, ' ')  // Replace hyphens with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getIconComponent = (iconName: string) => {
    switch(iconName) {
      case 'dollar': return <DollarSign className="w-6 h-6 text-green-400" />;
      case 'users': return <Users className="w-6 h-6 text-blue-400" />;
      case 'target': return <Target className="w-6 h-6 text-red-400" />;
      case 'trending': return <TrendingUp className="w-6 h-6 text-purple-400" />;
      default: return <Zap className="w-6 h-6 text-[#FDB913]" />;
    }
  };

  const getThinkingMessage = () => {
    if (thinkingProgress < 25) return "Initializing AI analysis...";
    if (thinkingProgress < 50) return `Analyzing performance data from last week...`;
    if (thinkingProgress < 75) return "Identifying patterns and opportunities...";
    if (thinkingProgress < 100) return "Generating personalized insights...";
    return "Analysis complete!";
  };

  if (!processedInsights || processedInsights.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <AnimatePresence mode="wait">

        {/* Entry State - AI Ready Card */}
        {currentState === 'entry' && (
          <motion.div
            key="entry"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex flex-col items-center justify-center text-center space-y-4 py-6"
          >
            {/* Animated AI Brain Icon */}
            <div className="relative">
              <motion.div
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                }}
                className="absolute inset-0 bg-gradient-to-r from-[#FDB913]/20 to-blue-500/20 rounded-full blur-2xl"
              />
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10 p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-2xl"
              >
                <Brain className="w-12 h-12 text-white" />
                <motion.div
                  className="absolute -top-2 -right-2"
                  animate={{ scale: [0, 1, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-6 h-6 text-[#FDB913]" />
                </motion.div>
              </motion.div>
            </div>

            {/* Title and Description */}
            <div className="space-y-3 max-w-xl">
              <h2 className="text-3xl font-bold text-gray-900">
                AI Performance Analysis
              </h2>
              <p className="text-gray-500 text-lg">
                I've analyzed your last week's data and identified <span className="text-[#FDB913] font-semibold">{processedInsights.length} key insights</span> to optimize your restaurant operations.
              </p>
            </div>

            {/* CTA Button */}
            <motion.button
              onClick={handleStartAnalysis}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-[#FDB913] hover:bg-[#e5a711] text-black font-bold rounded-lg shadow-lg flex items-center gap-3 text-lg"
            >
              <Zap className="w-5 h-5" />
              Generate My AI Insights
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        )}

        {/* Thinking State - AI Processing Animation */}
        {currentState === 'thinking' && (
          <motion.div
            key="thinking"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center text-center space-y-6 py-8"
          >
            {/* Scanning Animation */}
            <div className="relative w-32 h-32">
              <motion.div
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <div className="w-full h-full rounded-full border-4 border-[#FDB913]/30" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-8 bg-gradient-to-b from-[#FDB913] to-transparent rounded-full" />
              </motion.div>
              <div className="absolute inset-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <Brain className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full max-w-md space-y-3">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#FDB913]"
                  initial={{ width: 0 }}
                  animate={{ width: `${thinkingProgress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-gray-500 text-sm">
                {getThinkingMessage()}
              </p>
            </div>

            {/* Data Points Animation */}
            <div className="flex items-center gap-8 text-gray-400 text-sm">
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="flex items-center gap-2"
              >
                <div className="w-2 h-2 bg-[#FDB913] rounded-full" />
                Analyzing trends
              </motion.div>
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.3 }}
                className="flex items-center gap-2"
              >
                <div className="w-2 h-2 bg-[#FDB913] rounded-full" />
                Identifying patterns
              </motion.div>
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.6 }}
                className="flex items-center gap-2"
              >
                <div className="w-2 h-2 bg-[#FDB913] rounded-full" />
                Generating insights
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Revealing State - Progressive Insight Display */}
        {currentState === 'revealing' && processedInsights && (
          <motion.div
            key="revealing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Header with Progress */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">AI Performance Insights</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">Insight</span>
                <span className="text-[#FDB913] font-bold">{currentInsightIndex + 1}</span>
                <span className="text-gray-500 text-sm">of {processedInsights.length}</span>
              </div>
            </div>

            {/* Current Insight Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentInsightIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative"
              >
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  {/* Category Badge */}
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      processedInsights[currentInsightIndex].icon === 'dollar' ? 'bg-green-100' :
                      processedInsights[currentInsightIndex].icon === 'users' ? 'bg-blue-100' :
                      processedInsights[currentInsightIndex].icon === 'target' ? 'bg-red-100' :
                      'bg-[#FDB913]/20'
                    }`}>
                      {getIconComponent(processedInsights[currentInsightIndex].icon)}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-gray-900">
                          {processedInsights[currentInsightIndex].title}
                        </h3>
                        <span className="px-3 py-1 bg-[#FDB913]/20 text-[#b8860b] text-xs font-medium rounded-full">
                          {formatCategoryLabel(processedInsights[currentInsightIndex].category)}
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {processedInsights[currentInsightIndex].content}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Animated accent */}
                <motion.div
                  className="absolute -bottom-1 left-4 right-4 h-1 bg-gradient-to-r from-[#FDB913] to-transparent rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                />
              </motion.div>
            </AnimatePresence>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between pt-4">
              <button
                onClick={handlePreviousInsight}
                disabled={currentInsightIndex === 0}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  currentInsightIndex === 0
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Previous
              </button>

              {/* Progress Dots */}
              <div className="flex items-center gap-2">
                {processedInsights.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setCurrentInsightIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentInsightIndex
                        ? 'w-8 bg-[#FDB913]'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  />
                ))}
              </div>

              {currentInsightIndex === processedInsights.length - 1 ? (
                <button
                  onClick={() => {
                    setCurrentInsightIndex(0);
                    setCurrentState('entry');
                  }}
                  className="px-4 py-2 bg-[#FDB913] hover:bg-[#e5a711] text-black font-semibold rounded-lg transition-all flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Start Over
                </button>
              ) : (
                <button
                  onClick={handleNextInsight}
                  className="px-4 py-2 bg-[#FDB913] text-black hover:bg-[#e5a711] font-semibold rounded-lg transition-all flex items-center gap-2"
                >
                  Next Insight
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}